import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReportLayout from './report/ReportLayout';
import {
  ReportLoadingState, UpgradeRequiredState, ReportNotFoundState,
} from './report/EmptyState';
import LetterAndSummaryTab from './report/sections/LetterAndSummaryTab';
import FamilyDetailTab from './report/sections/FamilyDetailTab';
import ActionPlan from './report/sections/ActionPlan';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VALID_SECTIONS = ['lettre', 'activite', 'rentabilite', 'stock', 'patientele', 'plan-action'];
const DEFAULT_SECTION = 'lettre';
const ELIGIBLE_PLANS = new Set(['pilotage', 'premium']);

/**
 * Page racine du rapport cabinet — orchestre :
 *   - Gating plan (Pilotage+ exclusivement, sinon UpgradeRequiredState)
 *   - Chargement du rapport (latest publié si pas de period en URL, sinon
 *     filtrage par period dans la liste publiée)
 *   - Routing des 6 sous-onglets via path params
 *   - Sélection de la section affichée
 */
export default function MonthlyReportPage({ user }) {
  const { period: urlPeriod, section: urlSection } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const currentSection = (urlSection && VALID_SECTIONS.includes(urlSection)) ? urlSection : DEFAULT_SECTION;
  const isEligible = ELIGIBLE_PLANS.has(user?.plan);

  useEffect(() => {
    if (!isEligible) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem('benchory_token');
    if (!token) { navigate('/login'); return; }
    const headers = { Authorization: `Bearer ${token}` };

    let cancelled = false;
    const load = async () => {
      try {
        if (urlPeriod) {
          const list = await fetch(`${API_URL}/client/monthly-reports`, { headers }).then((r) => r.json());
          const match = (Array.isArray(list) ? list : []).find((r) => r.period === urlPeriod);
          if (!match) {
            if (!cancelled) { setNotFound(true); setLoading(false); }
            return;
          }
          const full = await fetch(`${API_URL}/client/monthly-reports/${match.id}`, { headers }).then((r) => r.json());
          if (cancelled) return;
          setReport(full);
          // Marquer le rapport comme consulté (best-effort, ne bloque pas le render)
          fetch(`${API_URL}/client/monthly-reports/${full.period}/mark-consulted`, { method: 'POST', headers }).catch(() => {});
          if (!urlSection) {
            navigate(`/espace-client/rapport-mensuel/${urlPeriod}/${DEFAULT_SECTION}`, { replace: true });
          }
        } else {
          const data = await fetch(`${API_URL}/client/monthly-reports/latest`, { headers }).then((r) => r.json());
          if (cancelled) return;
          if (data?.available && data.report) {
            setReport(data.report);
            fetch(`${API_URL}/client/monthly-reports/${data.report.period}/mark-consulted`, { method: 'POST', headers }).catch(() => {});
            navigate(`/espace-client/rapport-mensuel/${data.report.period}/${DEFAULT_SECTION}`, { replace: true });
          } else {
            setNotFound(true);
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) setNotFound(true);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPeriod, isEligible]);

  const goBackToPortal = () => navigate('/espace-client');

  if (!isEligible) {
    return <UpgradeRequiredState onExit={goBackToPortal} />;
  }
  if (loading) return <ReportLoadingState />;
  if (notFound || !report) {
    return <ReportNotFoundState period={urlPeriod} onExit={goBackToPortal} />;
  }

  return (
    <ReportLayout
      period={report.period}
      codeOfficine={report.code_officine}
      currentSection={currentSection}
      establishmentName={report.establishment_name}
      onSectionChange={(s) =>
        navigate(`/espace-client/rapport-mensuel/${report.period}/${s}`)
      }
      onExit={goBackToPortal}
    >
      {currentSection === 'lettre' && <LetterAndSummaryTab report={report} />}
      {currentSection === 'activite' && <FamilyDetailTab family="activite" report={report} />}
      {currentSection === 'rentabilite' && <FamilyDetailTab family="rentabilite" report={report} />}
      {currentSection === 'stock' && <FamilyDetailTab family="stock" report={report} />}
      {currentSection === 'patientele' && <FamilyDetailTab family="patientele" report={report} />}
      {currentSection === 'plan-action' && <ActionPlan report={report} />}
    </ReportLayout>
  );
}
